/**
 * UI-6b Task 10 — CsatScore widget renderer.
 *
 * Aggregate CSAT score (0-5) over the configured window, displayed as a
 * PrimeReact Knob. Data sourced from `/csat/summary?from=<N-days-ago>&to=<now>`
 * which returns per-(agent, queue) rows with `avgScore` + `surveyCount`. The
 * widget computes a weighted average across all rows so the gauge reflects
 * tenant-wide CSAT, not a per-agent figure.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Knob } from 'primereact/knob'
import { api } from '../../../../api/client.js'

export interface CsatScoreConfig {
  days: number
}

interface CsatRow {
  agentId: string | null
  agentName: string | null
  queueId: string | null
  queueName: string | null
  surveyCount: number | string
  avgScore: number | string | null
  minScore?: number | string | null
  maxScore?: number | string | null
  fcrPct?: number | string | null
}

interface CsatResponse {
  rows: CsatRow[]
  scale: number
}

function buildRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

function toNumber(v: number | string | null | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : Number.parseFloat(String(v)) || 0
}

/**
 * Weighted mean of per-row avgScore weighted by surveyCount. If every row
 * has zero surveys (or rows is empty) returns null so the renderer can show
 * an empty state instead of a misleading 0.
 */
function weightedAverage(rows: CsatRow[]): number | null {
  let weight = 0
  let total = 0
  for (const r of rows) {
    const n = toNumber(r.surveyCount)
    const s = toNumber(r.avgScore)
    if (n > 0) {
      weight += n
      total += s * n
    }
  }
  if (weight === 0) return null
  return total / weight
}

export function CsatScoreRenderer({ config }: { config: CsatScoreConfig }): JSX.Element {
  const [rows, setRows] = useState<CsatRow[] | null>(null)
  const [scale, setScale] = useState<number>(5)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const days = config.days > 0 ? config.days : 7

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const { from, to } = buildRange(days)
    const query = new URLSearchParams({ from, to })

    api
      .get<CsatResponse>(`/csat/summary?${query.toString()}`)
      .then((resp) => {
        if (!cancelled) {
          setRows(resp.rows)
          // Defensive fallback: a rolling deploy can briefly serve an older
          // API build whose /csat/summary response predates the scale field.
          setScale(resp.scale ?? 5)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load CSAT')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [days])

  const score = useMemo(() => (rows ? weightedAverage(rows) : null), [rows])

  if (loading) {
    return (
      <div data-testid="csat-score-loading" className="text-sm text-gray-500">
        Loading CSAT…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="csat-score-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }
  if (score == null) {
    return (
      <div data-testid="csat-score-empty" className="text-sm text-gray-500">
        No CSAT responses in the selected window.
      </div>
    )
  }

  return (
    <div
      data-testid="csat-score-widget"
      className="flex h-full w-full flex-col items-center justify-center gap-2"
    >
      <Knob
        value={Number(score.toFixed(2))}
        min={0}
        max={scale}
        step={0.1}
        valueTemplate={`{value}/${scale}`}
        readOnly
      />
      <div className="text-xs text-gray-500">Avg CSAT — last {days} day{days === 1 ? '' : 's'}</div>
    </div>
  )
}
